import type { MouseEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { trackSearchCalendarEventOverlayHover } from 'analytics/snowplow/events/searchCalendarEventOverlayEvents'

/** Stamped on every highlighted day cell by `CalendarGrid`. */
const EVENT_OVERLAY_SELECTOR = '[data-event-overlay]'

function getHoveredEventId(target: EventTarget | null): string | undefined {
  if (!(target instanceof Element)) return undefined

  return target.closest<HTMLElement>(EVENT_OVERLAY_SELECTOR)?.dataset.eventOverlay
}

/**
 * Tracks pointer entry into a highlighted date range, via delegation on a wrapper
 * around the calendar rather than per-day handlers.
 *
 * Moving between days inside the same range is one hover; leaving the range and
 * coming back is a second. Returns handlers for a wrapping element.
 */
function useSearchCalendarEventHoverTracking(placeIds: ReadonlyArray<string>, isEnabled: boolean) {
  const hoveredEventIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!isEnabled) hoveredEventIdRef.current = undefined
  }, [isEnabled])

  const onMouseOver = useCallback((mouseEvent: MouseEvent<HTMLElement>) => {
    if (!isEnabled) return

    const eventId = getHoveredEventId(mouseEvent.target)
    if (!eventId || eventId === hoveredEventIdRef.current) return

    hoveredEventIdRef.current = eventId
    trackSearchCalendarEventOverlayHover({ calendarEventId: eventId, placeIds })
  }, [isEnabled, placeIds])

  const onMouseOut = useCallback((mouseEvent: MouseEvent<HTMLElement>) => {
    // `relatedTarget` is where the pointer landed — still inside the range means no exit.
    if (getHoveredEventId(mouseEvent.relatedTarget)) return

    hoveredEventIdRef.current = undefined
  }, [])

  return useMemo(() => ({ onMouseOut, onMouseOver }), [onMouseOut, onMouseOver])
}

export default useSearchCalendarEventHoverTracking
